/* eslint-disable no-unused-vars */
import React, { ReactNode, useState } from 'react';
import { Box, Button, Flex, Image, Text } from '@chakra-ui/react';
import { CloudArrowUp, File as FileIcon, X } from 'phosphor-react';

interface UploadDropzoneProps {
  inputId: string;
  title?: string;
  description?: string;
  isInvalid?: boolean;
  isDisabled?: boolean;
  icon?: ReactNode;
  compact?: boolean;
  onFiles?: (files: FileList) => void;
}

const IconTile = ({ children, size = 40 }: { children: ReactNode; size?: number }) => (
  <Flex
    align="center"
    justify="center"
    w={`${size}px`}
    h={`${size}px`}
    flexShrink={0}
    borderRadius="md"
    border="1px solid"
    borderColor="line.input"
    bg="surface.base"
    boxShadow="control"
    color="content.body"
  >
    {children}
  </Flex>
);

export const UploadDropzone = ({
  inputId,
  title = 'Clique para enviar',
  description = 'PDF, PNG ou JPG',
  isInvalid,
  isDisabled,
  icon,
  compact = false,
  onFiles,
}: UploadDropzoneProps) => {
  const [dragging, setDragging] = useState(false);

  let borderColor = 'line.ring';
  if (isInvalid) borderColor = 'state.destructive';
  if (dragging) borderColor = 'content.strong';

  const dragProps = onFiles
    ? {
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          if (!isDisabled) setDragging(true);
        },
        onDragLeave: () => setDragging(false),
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          setDragging(false);
          if (!isDisabled && e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
        },
      }
    : {};

  return (
    <Box
      as="label"
      htmlFor={inputId}
      display="flex"
      flexDirection={compact ? 'row' : 'column'}
      alignItems="center"
      justifyContent={compact ? 'flex-start' : 'center'}
      gap="12px"
      w="100%"
      px={compact ? '12px' : '24px'}
      py={compact ? '8px' : '24px'}
      minH={compact ? '38px' : undefined}
      borderRadius={compact ? 'lg' : 'xl'}
      border="1px dashed"
      borderColor={borderColor}
      bg={dragging ? 'surface.selected' : 'surface.base'}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      opacity={isDisabled ? 0.64 : 1}
      textAlign={compact ? 'left' : 'center'}
      transition="background 0.15s ease, border-color 0.15s ease"
      _hover={isDisabled ? undefined : { bg: 'surface.selected' }}
      sx={{ '&:has(input:focus-visible)': { boxShadow: 'outline' } }}
      {...dragProps}
    >
      <IconTile size={compact ? 32 : 40}>
        {icon ?? <CloudArrowUp size={compact ? 16 : 20} />}
      </IconTile>
      <Box
        minW={0}
        display="flex"
        flexDirection="column"
        gap="2px"
        alignItems={compact ? 'flex-start' : 'center'}
      >
        <Text fontSize="13px" lineHeight="20px" color="content.body">
          <Text as="span" fontWeight={500} color="content.strong">
            {title}
          </Text>
          {!compact && ' ou arraste e solte'}
        </Text>
        <Text fontSize="12px" lineHeight="16px" color="content.subtle">
          {description}
        </Text>
      </Box>
    </Box>
  );
};

interface UploadPreviewRowProps {
  name: string;
  meta?: string;
  previewSrc?: string;
  onRemove?: () => void;
  onOpen?: () => void;
  replaceInputId?: string;
  isImage?: boolean;
}

export const UploadPreviewRow = ({
  name,
  meta,
  previewSrc,
  onRemove,
  onOpen,
  replaceInputId,
  isImage = true,
}: UploadPreviewRowProps) => (
  <Flex
    align="center"
    gap="12px"
    p="8px"
    pr="12px"
    borderRadius="lg"
    border="1px solid"
    borderColor="line.input"
    bg="surface.base"
    boxShadow="control"
    minW={0}
  >
    {isImage && previewSrc ? (
      <Box
        w="40px"
        h="40px"
        flexShrink={0}
        borderRadius="md"
        border="1px solid"
        borderColor="line.hairline"
        overflow="hidden"
      >
        <Image src={previewSrc} alt="" w="100%" h="100%" objectFit="cover" />
      </Box>
    ) : (
      <IconTile>
        <FileIcon size={16} />
      </IconTile>
    )}
    <Box flex="1" minW={0}>
      <Text fontSize="13px" lineHeight="20px" fontWeight={500} color="content.strong" isTruncated>
        {name}
      </Text>
      {meta && (
        <Text
          fontFamily="mono"
          fontSize="12px"
          lineHeight="16px"
          color="content.subtle"
          isTruncated
        >
          {meta}
        </Text>
      )}
    </Box>
    <Flex align="center" gap="4px" flexShrink={0}>
      {onOpen && (
        <Button type="button" size="xs" variant="outline" onClick={onOpen}>
          Ver
        </Button>
      )}
      {replaceInputId && (
        <Button as="label" htmlFor={replaceInputId} size="xs" variant="outline" cursor="pointer">
          Trocar
        </Button>
      )}
      {onRemove && (
        <Button
          type="button"
          size="xs"
          variant="ghost"
          aria-label="Remover arquivo"
          onClick={onRemove}
          px="0"
          minW="30px"
        >
          <X size={14} />
        </Button>
      )}
    </Flex>
  </Flex>
);
